import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  // Movement, kept small on purpose.
  //
  // A press should feel answered, not animated: 150ms, a 2% squash on
  // :active, and colour that eases rather than snaps. Anything larger reads as
  // a toy on a screen somebody uses for eight hours.
  //
  // motion-reduce:transition-none and motion-reduce:transform-none honour the
  // operating system's "reduce motion" setting. For a driver with vestibular
  // sensitivity that is not a preference, and a button that ignores it is a
  // button that hurts to use.
  //
  // transform-gpu keeps the squash off the layout: it composites rather than
  // reflowing, so a table of forty rows with a button in each does not relayout
  // when one is pressed.
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium',
    'transition-all duration-150 ease-out transform-gpu',
    'active:scale-[0.98] active:duration-75',
    'motion-reduce:transition-none motion-reduce:transform-none motion-reduce:active:scale-100',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    // A disabled button should look unavailable and stay still - no hover, no
    // press, no lift.
    'disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none',
    '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
    // Icons follow the button rather than sitting still inside a moving box.
    '[&_svg]:transition-transform [&_svg]:duration-150',
  ].join(' '),
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm hover:shadow-md hover:-translate-y-px motion-reduce:hover:translate-y-0',
        // The gold call-to-action carries the most weight, so it lifts furthest.
        gold:
          'bg-gold text-navy-900 hover:bg-gold-500 shadow-md hover:shadow-lg hover:-translate-y-0.5 motion-reduce:hover:translate-y-0',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground hover:border-ring/40',
        // No lift and no shadow: a ghost sits in the page, it does not float.
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-sm hover:shadow-md hover:-translate-y-px motion-reduce:hover:translate-y-0',
        // A link is text. It underlines; it does not move.
        link: 'text-primary underline-offset-4 hover:underline active:scale-100',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        xl: 'h-12 rounded-md px-10 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
